import {
  CaretRightIcon,
  FileIcon,
  FolderIcon,
  FolderOpenIcon,
} from "@phosphor-icons/react";
import { type KeyboardEvent, type MouseEvent, useEffect, useRef } from "react";
import type {
  ProjectSummary,
  SlimSessionRef,
} from "../../generated/api/types.gen";
import type { GalleryPageState } from "../../hooks/useGalleryPage";
import { useInlineRename } from "../../hooks/useInlineRename";
import { shouldShowSharedSection } from "../../lib/asset-scope";
import { DrawerToggleButton } from "../DrawerToggleButton";
import { getDesignTitle, startSessionDrag } from "./galleryTree";
import { GalleryTreeProvider, useGalleryTree } from "./galleryTreeContext";
import { useAssetDropTarget } from "./useAssetDropTarget";
import { useSessionDropTarget } from "./useSessionDropTarget";

/** Indent per nesting level under a section. Depth 1 (root) aligns with section headers. */
const TREE_DEPTH_PX = 14;

function treeRowIndentPx(depth: number): number {
  return Math.max(0, depth - 1) * TREE_DEPTH_PX;
}
/** Folder/file icon size — used for folder and file rows. */
const FOLDER_ICON_SIZE = 15;
/** Caret size for per-row expand control (matches section carats). */
const ROW_CARET_SIZE = 14;
/** Delay (ms) before a single click acts, so a double click can cancel it. */
const DOUBLE_CLICK_MS = 250;

interface GalleryProjectDrawerProps {
  gallery: GalleryPageState;
}

/**
 * Disambiguates single vs. double click on a row: `onSingleClick` runs only if
 * no second click arrives within DOUBLE_CLICK_MS, so double-click-to-rename
 * doesn't also fire the single-click action (navigation / selection).
 */
function useSingleOrDoubleClick(
  onSingleClick: () => void,
  onDoubleClick: () => void,
) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleClick = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onSingleClick();
    }, DOUBLE_CLICK_MS);
  };

  const handleDoubleClick = (event: MouseEvent) => {
    event.preventDefault(); // suppress text selection on the row
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onDoubleClick();
  };

  return { handleClick, handleDoubleClick };
}

function DrawerHeader({
  isCollapsed,
  onToggleCollapsed,
}: {
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  return (
    <div
      className={`flex shrink-0 items-center px-6 py-4 ${
        isCollapsed ? "justify-center" : "justify-end gap-2"
      }`}
    >
      <DrawerToggleButton
        direction={isCollapsed ? "expand" : "collapse"}
        panel="project drawer"
        onClick={onToggleCollapsed}
        className={isCollapsed ? "p-0" : "p-2"}
      />
    </div>
  );
}

/** Top-level section label ("My Projects" / "Shared Projects") with a divider. */
function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-2 pt-2">
      <div className="pb-2 text-xl font-bold leading-7 text-[var(--text-primary)]">
        {label}
      </div>
      <div className="h-px bg-[var(--stroke-field)]" aria-hidden />
    </div>
  );
}

/**
 * Collapsible "Designs" sub-header. Holds the folders + standalone designs of
 * its section. Styled to match the standalone "Assets" link (same label size,
 * trailing caret), but the caret toggles expansion rather than navigating.
 */
function DesignsHeader({
  isExpanded,
  isDragOver,
  isForbidden,
  isSelected,
  onSelect,
  onToggle,
}: {
  isExpanded: boolean;
  isDragOver: boolean;
  isForbidden: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onToggle: () => void;
}) {
  const idleClass = isSelected
    ? "rounded-md bg-[var(--accent-primary)]/55 text-[var(--text-primary)]"
    : "rounded-md text-[var(--text-primary)] hover:bg-[var(--bg-field)]/50";

  return (
    <div className="px-2">
      <div
        className={`flex items-center justify-between gap-2 px-1 py-2 text-sm font-semibold transition ${
          isDragOver
            ? "rounded-md bg-[var(--accent-primary)]/80 text-[var(--text-primary)]"
            : isForbidden
              ? "rounded-md bg-red-900/30 text-[var(--text-secondary)]"
              : idleClass
        }`}
      >
        <button
          type="button"
          onClick={onSelect}
          className="min-w-0 flex-1 cursor-pointer truncate rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1c2228]"
        >
          <span className="text-lg font-semibold leading-6">Designs</span>
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          className="shrink-0 rounded p-1 text-[var(--text-secondary)] transition hover:bg-[var(--bg-field)] hover:text-[var(--text-primary)]"
          aria-label={isExpanded ? "Collapse Designs" : "Expand Designs"}
        >
          <CaretRightIcon
            size={14}
            className={`transition-transform ${isExpanded ? "rotate-90" : ""}`}
          />
        </button>
      </div>
    </div>
  );
}

function MyProjectsList({
  rootProjects,
  rootSessions,
}: {
  rootProjects: ProjectSummary[];
  rootSessions: SlimSessionRef[];
}) {
  const { onSessionClick } = useGalleryTree();
  return (
    <div className="mt-1 space-y-1">
      {rootProjects.map((project) => (
        <GalleryProjectTreeItem key={project.id} project={project} depth={2} />
      ))}
      {rootSessions.map((session) => (
        <GallerySessionTreeItem
          key={session.session_id}
          session={session}
          depth={2}
          onClick={() => onSessionClick(session.session_id)}
        />
      ))}
    </div>
  );
}

function assetSectionButtonClass(isSelected: boolean, isOver: boolean) {
  const base =
    "flex w-full items-center justify-between gap-2 rounded-md px-1 py-2 text-left transition outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1c2228]";
  // Drop-hover wins over selection so dragging into an already-active
  // section still gives drag feedback. Falls back to selection, then idle.
  if (isOver)
    return `${base} bg-[var(--accent-primary)]/80 text-[var(--text-primary)]`;
  if (isSelected)
    return `${base} bg-[var(--accent-primary)]/55 text-[var(--text-primary)]`;
  return `${base} text-[var(--text-primary)] hover:bg-[var(--bg-field)]/50`;
}

interface AssetSectionLinkProps {
  label: string;
  isSelected: boolean;
  onSelect: () => void;
  /** Tailwind spacing above the section (varies by neighbor). */
  spacingTop: string;
  /** Wires a drop target onto the section. Omit for read-only sections. */
  onShareAssetWithOrg?: (assetId: string) => void | Promise<void>;
  /** When true, the most recent share-to-org drop failed. */
  shareFailed?: boolean;
}

function AssetSectionLink({
  label,
  isSelected,
  onSelect,
  spacingTop,
  onShareAssetWithOrg,
  shareFailed,
}: AssetSectionLinkProps) {
  const { isOver, dropProps } = useAssetDropTarget<HTMLDivElement>(
    onShareAssetWithOrg ?? null,
  );
  return (
    <div className={`${spacingTop} px-2`} {...dropProps}>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={isSelected}
        className={assetSectionButtonClass(isSelected, isOver)}
      >
        <span className="min-w-0 truncate text-lg font-semibold leading-6">
          {label}
        </span>
      </button>
      {shareFailed ? (
        <p className="mt-1 px-2 text-sm text-red-400" role="alert">
          Couldn't share that asset. Please try again.
        </p>
      ) : null}
    </div>
  );
}

/**
 * "My Projects" section: a plain header, a collapsible "Designs" sub-header
 * holding the user's root folders + standalone designs, and a standalone
 * "Assets" link. The Designs header doubles as the personal-root drop target.
 */
function MyProjectsSection({ gallery }: { gallery: GalleryPageState }) {
  const { onMoveSessionToProject } = useGalleryTree();
  const {
    isOver: isDragOver,
    isForbidden,
    dropProps,
  } = useSessionDropTarget<HTMLDivElement>(null, onMoveSessionToProject);

  return (
    <div>
      <SectionHeader label="My Projects" />

      <div className="mt-4 pl-3">
        <div {...dropProps}>
          <DesignsHeader
            isExpanded={gallery.isRootExpanded}
            isDragOver={isDragOver}
            isForbidden={isForbidden}
            isSelected={gallery.selectedNode.type === "root"}
            onSelect={gallery.selectRoot}
            onToggle={() => gallery.setIsRootExpanded((e: boolean) => !e)}
          />
          {gallery.isRootExpanded && (
            <MyProjectsList
              rootProjects={gallery.rootProjects}
              rootSessions={gallery.rootSessions}
            />
          )}
        </div>

        <AssetSectionLink
          label="Assets"
          spacingTop="mt-4"
          isSelected={
            gallery.selectedNode.type === "assets" &&
            gallery.selectedNode.scope === "personal"
          }
          onSelect={() => gallery.selectAssets("personal")}
        />
      </div>
    </div>
  );
}

/**
 * "Shared Projects" section: mirrors "My Projects" for the active org. The
 * org's auto-created root folder is no longer surfaced by name — its children
 * live directly under a collapsible "Designs" sub-header, and the section is
 * labelled "Shared Projects" rather than the org name.
 */
function SharedProjectsSection({ gallery }: { gallery: GalleryPageState }) {
  const { onMoveSessionToProject } = useGalleryTree();
  const orgRoot = gallery.orgRoot;
  const { isOver: isDragOver, dropProps } =
    useSessionDropTarget<HTMLDivElement>(
      orgRoot?.id ?? null,
      onMoveSessionToProject,
      { isOrgShared: true },
    );

  return (
    <div className="mt-6">
      <SectionHeader label="Shared Projects" />

      <div className="mt-4 pl-3">
        {orgRoot && (
          <div {...dropProps}>
            <DesignsHeader
              isExpanded={gallery.isOrgRootExpanded}
              isDragOver={isDragOver}
              isForbidden={false}
              isSelected={
                gallery.selectedNode.type === "project" &&
                gallery.selectedNode.projectId === orgRoot.id
              }
              onSelect={() => gallery.selectProject(orgRoot.id)}
              onToggle={() => gallery.setIsOrgRootExpanded((e: boolean) => !e)}
            />
            {gallery.isOrgRootExpanded && (
              <div className="mt-1">
                <ProjectChildren projectId={orgRoot.id} depth={2} />
              </div>
            )}
          </div>
        )}

        {gallery.activeOrgName && (
          <AssetSectionLink
            label="Assets"
            spacingTop="mt-4"
            isSelected={
              gallery.selectedNode.type === "assets" &&
              gallery.selectedNode.scope === "organization"
            }
            onSelect={() => gallery.selectAssets("organization")}
            onShareAssetWithOrg={gallery.handleShareAssetWithOrg}
            shareFailed={gallery.shareAssetFailed}
          />
        )}
      </div>
    </div>
  );
}

function DrawerTreeContent({ gallery }: { gallery: GalleryPageState }) {
  const showShared = shouldShowSharedSection(gallery);

  return (
    <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto px-6 pb-9">
      <MyProjectsSection gallery={gallery} />
      {showShared && <SharedProjectsSection gallery={gallery} />}
    </div>
  );
}

export function GalleryProjectDrawer({ gallery }: GalleryProjectDrawerProps) {
  const onToggleCollapsed = () => gallery.setIsDrawerCollapsed((c) => !c);

  const contextValue = {
    treeData: gallery.treeData,
    expandedProjectIds: gallery.expandedProjectIds,
    selectedNode: gallery.selectedNode,
    onSelectProject: gallery.selectProject,
    onToggleProject: gallery.toggleProject,
    onRenameProject: gallery.handleRenameProject,
    onRenameSession: gallery.handleRenameSession,
    onMoveSessionToProject: gallery.handleMoveSessionToProject,
    onSessionClick: gallery.handleSessionClick,
    selectedSessionId: gallery.selectedSessionId,
  };

  return (
    <GalleryTreeProvider value={contextValue}>
      <aside
        className={`flex h-full shrink-0 flex-col bg-[#1c2228] transition-[width] duration-300 ease-out ${
          gallery.isDrawerCollapsed ? "w-[72px]" : "w-[clamp(20rem,30vw,455px)]"
        }`}
      >
        <DrawerHeader
          isCollapsed={gallery.isDrawerCollapsed}
          onToggleCollapsed={onToggleCollapsed}
        />

        {!gallery.isDrawerCollapsed && <DrawerTreeContent gallery={gallery} />}
      </aside>
    </GalleryTreeProvider>
  );
}

interface BaseGalleryProjectTreeItemProps {
  project: ProjectSummary;
  depth: number;
}

interface ProjectRowState {
  hasChildren: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  drag: { isOver: boolean; isForbidden: boolean };
}

interface ProjectRowProps {
  project: ProjectSummary;
  depth: number;
  state: ProjectRowState;
  dropProps: React.HTMLAttributes<HTMLDivElement>;
  children: React.ReactNode;
}

function ProjectExpandButton({
  hasChildren,
  isExpanded,
  isSelected,
  projectId,
  onToggleProject,
}: {
  hasChildren: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  projectId: string;
  onToggleProject: (projectId: string) => void;
}) {
  if (!hasChildren) return null;

  return (
    <button
      type="button"
      onClick={() => onToggleProject(projectId)}
      className={`shrink-0 rounded px-1.5 transition ${
        isSelected
          ? "text-[var(--text-primary)]"
          : "text-[var(--text-secondary)]"
      }`}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? "Collapse project" : "Expand project"}
    >
      <CaretRightIcon
        size={ROW_CARET_SIZE}
        className={`transition-transform ${isExpanded ? "rotate-90" : ""}`}
      />
    </button>
  );
}

function ProjectNameCell({
  project,
  isSelected,
  Icon,
}: {
  project: ProjectSummary;
  isSelected: boolean;
  Icon: typeof FolderOpenIcon | typeof FolderIcon;
}) {
  const { onSelectProject, onRenameProject } = useGalleryTree();
  const {
    isEditing,
    draftName,
    setDraftName,
    startEditing,
    saveName,
    handleNameKeyDown,
    focusOnMount,
  } = useInlineRename(project.id, project.name, onRenameProject);
  const { handleClick, handleDoubleClick } = useSingleOrDoubleClick(
    () => !isEditing && onSelectProject(project.id),
    () => !isEditing && startEditing(),
  );

  return (
    // biome-ignore lint/a11y/useSemanticElements: double-click rename pattern requires div with role="button"
    <div
      role="button"
      tabIndex={0}
      title="Double-click to rename"
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={(e) => {
        if (isEditing) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectProject(project.id);
          return;
        }
        if (e.key === "F2") {
          e.preventDefault();
          startEditing();
        }
      }}
      className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-[16px] leading-6"
    >
      <Icon
        size={FOLDER_ICON_SIZE}
        weight={isSelected ? "fill" : "regular"}
        className={isSelected ? "shrink-0 text-white" : "shrink-0"}
      />
      {isEditing ? (
        <input
          ref={focusOnMount}
          value={draftName}
          onChange={(event) => setDraftName(event.target.value)}
          onBlur={saveName}
          onKeyDown={handleNameKeyDown}
          onClick={(event) => event.stopPropagation()}
          className="min-w-0 flex-1 rounded bg-[var(--bg-modal)] px-1 py-0.5 text-[var(--text-primary)] outline-none ring-1 ring-[var(--accent-primary)]"
        />
      ) : (
        <span className="min-w-0 truncate">{project.name}</span>
      )}
    </div>
  );
}

function ProjectRow({
  project,
  depth,
  state,
  dropProps,
  children,
}: ProjectRowProps) {
  const { onToggleProject } = useGalleryTree();
  const { hasChildren, isExpanded, isSelected, drag } = state;
  const rowIdle =
    "text-[var(--text-primary)] hover:bg-[var(--bg-field)]/70 hover:text-[var(--text-primary)]";
  const baseRow =
    "flex min-w-0 w-full items-stretch gap-0 rounded-md transition ";
  const rowClass = drag.isOver
    ? `${baseRow}bg-[var(--accent-primary)]/80 text-[var(--text-primary)]`
    : drag.isForbidden
      ? `${baseRow}bg-red-900/30 text-[var(--text-secondary)]`
      : isSelected
        ? `${baseRow}bg-[var(--accent-primary)]/55 text-[var(--text-primary)]`
        : `${baseRow}${rowIdle}`;

  return (
    <div
      className="min-w-0"
      style={{ paddingLeft: `${treeRowIndentPx(depth)}px` }}
    >
      <div
        {...dropProps}
        className={`flex min-w-0 w-full items-stretch gap-0 rounded-md transition ${rowClass}`}
      >
        {children}
        <ProjectExpandButton
          hasChildren={hasChildren}
          isExpanded={isExpanded}
          isSelected={isSelected}
          projectId={project.id}
          onToggleProject={onToggleProject}
        />
      </div>
    </div>
  );
}

function ProjectChildren({
  projectId,
  depth,
}: {
  projectId: string;
  depth: number;
}) {
  const { treeData, onSessionClick } = useGalleryTree();
  const childProjects = treeData.projectsByParentId.get(projectId) ?? [];
  const childSessions = treeData.sessionsByProjectId.get(projectId) ?? [];

  return (
    <div className="mt-1 space-y-1">
      {childProjects.map((childProject) => (
        <GalleryProjectTreeItem
          key={childProject.id}
          project={childProject}
          depth={depth}
        />
      ))}
      {childSessions.map((session) => (
        <GallerySessionTreeItem
          key={session.session_id}
          session={session}
          depth={depth}
          onClick={() => onSessionClick(session.session_id)}
        />
      ))}
    </div>
  );
}

function GalleryProjectTreeItem({
  project,
  depth,
}: BaseGalleryProjectTreeItemProps) {
  const { treeData, expandedProjectIds, selectedNode, onMoveSessionToProject } =
    useGalleryTree();
  const hasChildren =
    (treeData.projectsByParentId.get(project.id) ?? []).length > 0 ||
    (treeData.sessionsByProjectId.get(project.id) ?? []).length > 0;
  const isExpanded = expandedProjectIds.has(project.id);
  const isSelected =
    selectedNode.type === "project" && selectedNode.projectId === project.id;
  const {
    isOver: isDragOver,
    isForbidden: isDragForbidden,
    dropProps,
  } = useSessionDropTarget<HTMLDivElement>(project.id, onMoveSessionToProject, {
    isOrgShared: project.is_shared_with_organization,
  });
  const Icon = isExpanded ? FolderOpenIcon : FolderIcon;
  return (
    <div>
      <ProjectRow
        project={project}
        depth={depth}
        state={{
          hasChildren,
          isExpanded,
          isSelected,
          drag: { isOver: isDragOver, isForbidden: isDragForbidden },
        }}
        dropProps={dropProps}
      >
        <ProjectNameCell
          project={project}
          isSelected={isSelected}
          Icon={Icon}
        />
      </ProjectRow>
      {isExpanded && (
        <ProjectChildren projectId={project.id} depth={depth + 1} />
      )}
    </div>
  );
}

interface GallerySessionTreeItemProps {
  session: SlimSessionRef;
  depth: number;
  onClick: () => void;
}

function sessionTreeRowKeyDown(
  isEditing: boolean,
  onClick: () => void,
  startEditing: () => void,
) {
  return (event: KeyboardEvent<HTMLDivElement>) => {
    if (isEditing) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
      return;
    }
    if (event.key === "F2") {
      event.preventDefault();
      startEditing();
    }
  };
}

function GallerySessionTreeItem({
  session,
  depth,
  onClick,
}: GallerySessionTreeItemProps) {
  const { onRenameSession, selectedSessionId } = useGalleryTree();
  const currentTitle = getDesignTitle(session);
  const isSelected = selectedSessionId === session.session_id;
  const {
    isEditing,
    draftName,
    setDraftName,
    startEditing,
    saveName,
    handleNameKeyDown,
    focusOnMount,
  } = useInlineRename(session.session_id, currentTitle, onRenameSession);
  const { handleClick, handleDoubleClick } = useSingleOrDoubleClick(
    () => !isEditing && onClick(),
    () => !isEditing && startEditing(),
  );

  const rowClass = isSelected
    ? "bg-[var(--accent-primary)]/55 text-[var(--text-primary)]"
    : "text-[var(--text-primary)] hover:bg-[var(--bg-field)]/70 hover:text-[var(--text-primary)]";

  return (
    <div style={{ paddingLeft: `${treeRowIndentPx(depth)}px` }}>
      {/* biome-ignore lint/a11y/useSemanticElements: double-click rename pattern requires div with role="button" */}
      <div
        role="button"
        tabIndex={0}
        draggable={!isEditing}
        title="Double-click to rename"
        onDragStart={(event) => startSessionDrag(session, event)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onKeyDown={sessionTreeRowKeyDown(isEditing, onClick, startEditing)}
        className={`flex min-w-0 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[16px] leading-6 transition ${rowClass}`}
      >
        <FileIcon size={FOLDER_ICON_SIZE} className="shrink-0" />
        {isEditing ? (
          <input
            ref={focusOnMount}
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            onBlur={saveName}
            onKeyDown={handleNameKeyDown}
            onClick={(event) => event.stopPropagation()}
            className="min-w-0 flex-1 rounded bg-[var(--bg-modal)] px-1 py-0.5 text-[var(--text-primary)] outline-none ring-1 ring-[var(--accent-primary)]"
          />
        ) : (
          <span className="min-w-0 truncate">{currentTitle}</span>
        )}
      </div>
    </div>
  );
}
